import {format, isSameMonth, parse, parseISO} from 'date-fns';

export const formatAPIDate = (dateString = '', formatString = 'LLL d, y') =>
  format(parse(dateString, 'y-LL-dd', new Date()), formatString);

export const formatAPIDateForInput = (dateString = '') => formatAPIDate(dateString, 'MM/dd/yyyy');

/**
 * Given a time string like HH:mm:ss format it as a human readable duration. If there is
 * no hours or minutes we will format the number of seconds, otherwise format the hours and
 * minutes.
 *
 * Examples:
 * - 02:07:00 => 2 hour 7 min
 * - 00:07:00 => 7 min
 * - 00:00:30 => 30 sec
 */
export const formatDuration = (duration?: string | null) => {
  if (!duration) return null;

  // Parse the time string into numbers
  const parts = duration.split(':');
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  // Number format can be either HH:mm:ss or mm:ss so parse out the correct part based on that
  if (parts.length === 3) {
    hours = parseInt(parts[0]);
    minutes = parseInt(parts[1]);
    seconds = parseInt(parts[2]);
  } else if (parts.length === 2) {
    minutes = parseInt(parts[0]);
    seconds = parseInt(parts[1]);
  }

  // If there are no hours or minutes we will try to parse seconds
  if (!(hours || minutes)) {
    if (!seconds) {
      return null;
    }

    return `${seconds} sec`;
  }

  // Otherwise we will use hours and/or minutes, depending on what we're given.
  const formatted: string[] = [];

  if (hours) {
    formatted.push(`${hours} hour`);
  }
  if (minutes) {
    formatted.push(`${minutes} min`);
  }

  return formatted.join(' ');
};

/**
 * Given two ISO date strings, this function will return a formatted string
 * representing the range between the two dates (eg, Mar 6-12, or May 29–Jun 3).
 */
export function getFormattedDateRange(startedOn = '', endedOn = '') {
  if (!startedOn && !endedOn) {
    return '';
  }

  const hasSameMonth = isSameMonth(parseISO(startedOn), parseISO(endedOn));

  return hasSameMonth
    ? `${format(parseISO(startedOn), 'MMM d')}–${format(parseISO(endedOn), 'd')}`
    : `${format(parseISO(startedOn), 'MMM d')}–${format(parseISO(endedOn), 'MMM d')}`;
}
const options = {year: 'numeric', month: 'short', day: 'numeric'} as Intl.DateTimeFormatOptions;

export const formattedDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
};

export const addDays = (date: string, days: number) => {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// convert from mm/dd/yyyy to yyyy-mm-dd
export const convertDateFormat = (date: string) => {
  const dateArray = date.split('/');
  return `${dateArray[2]}-${dateArray[0]}-${dateArray[1]}`;
};

export const daysBetweenDates = (date1: Date, date2: Date) => {
  const timeDiff = Math.abs(date1.getTime() - date2.getTime());
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};
